import React, { useState, cloneElement } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ChicaEquipos from "../static/media/ChicaEquipos.webp";

const MotionBox = motion(Box);

const modules = [
  {
    id: "facturacion",
    tag: "VENTAS ELECTRÓNICAS",
    number: "01",
    title: "Facturación y Documentos Electrónicos",
    summary:
      "Emite comprobantes válidos ante el SRI en segundos, sin errores ni reprocesos.",
    features: [
      "Generación rápida sin errores de codificación",
      "Emisión de facturas, notas de crédito y débito",
      "Historial completo de documentos emitidos",
      "Exportación en XML y PDF con un clic",
      "Envío automático al correo del cliente",
    ],
    accent: "#1E40AF",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect
          x="4"
          y="3"
          width="20"
          height="22"
          rx="3"
          stroke="white"
          strokeWidth="1.8"
        />
        <path
          d="M8 9h12M8 13h8M8 17h10"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <circle cx="20" cy="19" r="5" fill="#4F8CFF" />
        <path
          d="M18 19l1.5 1.5L22 17"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "proformas",
    tag: "COTIZACIONES RÁPIDAS",
    number: "02",
    title: "Gestión de Proformas",
    summary:
      "Convierte cotizaciones en facturas reales con un solo clic, sin reescribir nada.",
    features: [
      "Creación de cotizaciones en segundos",
      "Estado de aprobación en tiempo real",
      "Conversión directa a factura sin pérdida de datos",
      "Términos y condiciones personalizables",
      "Historial por cliente y por período",
    ],
    accent: "#1A3D8F",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path
          d="M6 5h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
          stroke="white"
          strokeWidth="1.8"
        />
        <path
          d="M9 10h10M9 14h6"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M17 17l3 3M17 20l3-3"
          stroke="#4F8CFF"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "inventario",
    tag: "CONTROL DE STOCK",
    number: "03",
    title: "Control de Inventario y Secciones",
    summary:
      "Tu stock siempre al día, con alertas automáticas antes de quedarte sin producto.",
    features: [
      "Organización por secciones y categorías",
      "Alertas de stock mínimo configurables",
      "Registro de entradas y salidas de mercancía",
      "Reportes de movimiento por período",
      "Ajuste de inventario desde el sistema",
    ],
    accent: "#1E40AF",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect
          x="4"
          y="12"
          width="8"
          height="12"
          rx="2"
          stroke="white"
          strokeWidth="1.8"
        />
        <rect
          x="10"
          y="8"
          width="8"
          height="16"
          rx="2"
          stroke="white"
          strokeWidth="1.8"
        />
        <rect
          x="16"
          y="4"
          width="8"
          height="20"
          rx="2"
          stroke="white"
          strokeWidth="1.8"
        />
        <path
          d="M4 22h20"
          stroke="#4F8CFF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "compras",
    tag: "GASTOS Y PROVEEDORES",
    number: "04",
    title: "Registro de Compras y Gastos",
    summary:
      "Controla cada centavo que entra y sale. Tu contabilidad, clara en todo momento.",
    features: [
      "Control detallado por proveedor",
      "Órdenes de compra con seguimiento",
      "Gestión de gastos operativos",
      "Conciliación automática con inventario",
      "Reportes de rentabilidad por período",
    ],
    accent: "#4F8CFF",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path
          d="M5 7h18l-2 10H7L5 7z"
          stroke="white"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle cx="10" cy="22" r="1.5" fill="white" />
        <circle cx="20" cy="22" r="1.5" fill="white" />
        <path
          d="M5 7L4 4H2"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M10 12l3 3 5-5"
          stroke="#4F8CFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

export default function ModulesSection() {
  const [active, setActive] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const mainGradient =
    "linear-gradient(180deg, rgba(244, 247, 251, 1) 0%, rgba(255, 255, 255, 1) 15%, rgba(209, 237, 250, 1) 100%)";

  return (
    <Box
      id="modulos"
      sx={{
        py: { xs: 6, md: 10 },
        background: mainGradient,
        position: "relative",
        overflow: "hidden",
        overflowX: "hidden",
      }}
    >
      {/* Fondo geométrico decorativo */}
      <Box
        sx={{
          position: "absolute",
          right: -120,
          top: "50%",
          transform: "translateY(-50%)",
          width: 500,
          height: 500,
          opacity: 0.03,
          pointerEvents: "none",
        }}
      >
        <svg viewBox="0 0 500 500" fill="none">
          <polygon
            points="250,20 480,140 480,360 250,480 20,360 20,140"
            fill="#1E40AF"
          />
        </svg>
      </Box>

      <Container maxWidth="lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.6rem" },
              color: "#1A2B4A",
              lineHeight: 1.1,
              mb: 1.5,
              maxWidth: 640,
            }}
          >
            Módulos del{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #1E40AF 0%, #4F8CFF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              sistema
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "#475569",
              fontSize: "1rem",
              lineHeight: 1.6,
              maxWidth: 560,
            }}
          >
            Cada área de tu negocio cubierta. FactuSysDR integra los módulos que
            una operación comercial realmente necesita, sin funciones
            innecesarias que compliquen el día a día.
          </Typography>
        </MotionBox>

        {/* CONTENIDO PRINCIPAL */}
        {isMobile ? (
          /* ================= MOBILE VIEW ================= */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* TABS MOBILE (En Grid de 4) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: { xs: 1, sm: 1.5 },
                width: "100%",
              }}
            >
              {modules.map((mod, i) => (
                <MotionBox
                  key={mod.id}
                  onClick={() => setActive(i)}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: "14px",
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor:
                      i === active
                        ? "rgba(30,64,175,0.7)"
                        : "rgba(44,95,190,0.15)",
                    bgcolor: i === active ? "#1E40AF" : "#fff",
                    boxShadow:
                      i === active ? "0 4px 15px rgba(30,64,175,0.2)" : "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor:
                        i === active
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(44,95,190,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background 0.2s",
                    }}
                  >
                    {cloneElement(mod.icon, {
                      style: {
                        width: 14,
                        height: 14,
                        filter:
                          i === active ? "none" : "saturate(0) opacity(0.5)",
                        transition: "filter 0.2s",
                      },
                    })}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      fontWeight: 700,
                      color: i === active ? "#fff" : "#64748B",
                      transition: "color 0.2s",
                      lineHeight: 1.1,
                      textAlign: "center",
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {mod.title.split(" ")[0]}
                  </Typography>
                </MotionBox>
              ))}
            </Box>

            {/* EXPANDED CONTENT PANEL MOBILE */}
            <AnimatePresence mode="wait">
              <MotionBox
                key={active}
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -15 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                sx={{
                  bgcolor: "#F8FAFF",
                  borderRadius: "20px",
                  p: { xs: 3, sm: 4 },
                  border: "1px solid rgba(44,95,190,0.09)",
                  position: "relative",
                  minHeight: 380,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Capa de Fondo para vector azul mobile */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "20px",
                    overflow: "hidden",
                    zIndex: 0,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -30,
                      right: -30,
                      width: 100,
                      height: 100,
                      opacity: 0.06,
                      pointerEvents: "none",
                    }}
                  >
                    <svg viewBox="0 0 140 140" fill="none">
                      <polygon
                        points="70,5 135,37 135,103 70,135 5,103 5,37"
                        fill="#1E40AF"
                      />
                    </svg>
                  </Box>
                </Box>

                {/* Contenido de Texto Mobile */}
                <Box sx={{ position: "relative", zIndex: 3 }}>
                  <Typography
                    component="h3"
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.5rem",
                      color: "#1A2B4A",
                      lineHeight: 1.1,
                      mb: 1,
                      maxWidth: "100%",
                    }}
                  >
                    {modules[active].title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#1E40AF",
                      fontWeight: 700,
                      fontSize: "0.93rem",
                      mb: 2,
                      lineHeight: 1.5,
                      maxWidth: {
                        xs: "calc(100% - 90px)",
                        sm: "calc(100% - 130px)",
                      }, // Deja aire arriba
                    }}
                  >
                    {modules[active].summary}
                  </Typography>

                  <Box
                    sx={{
                      width: {
                        xs: "calc(100% - 110px)",
                        sm: "calc(100% - 150px)",
                      }, // La línea se detiene antes de la chica
                      height: "1px",
                      bgcolor: "rgba(44,95,190,0.1)",
                      mb: 2,
                    }}
                  />

                  {/* CAJA DE VIÑETAS CON LÍMITE ESTRICTO DE ANCHO */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      width: {
                        xs: "calc(100% - 125px)",
                        sm: "calc(100% - 160px)",
                      }, // Jamás tocará la imagen
                    }}
                  >
                    {modules[active].features.map((feat, i) => (
                      <MotionBox
                        key={feat}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05, duration: 0.3 }}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "5px",
                            bgcolor: "#1E40AF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: "2px",
                          }}
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                          >
                            <path
                              d="M2 5l2.5 2.5L8 3"
                              stroke="white"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Box>
                        <Typography
                          sx={{
                            color: "#334155",
                            fontSize: "0.85rem",
                            lineHeight: 1.4,
                            fontWeight: 500,
                          }}
                        >
                          {feat}
                        </Typography>
                      </MotionBox>
                    ))}
                  </Box>
                </Box>

                {/* IMAGEN CHICA MOBILE CON CONTENEDOR RESTRINGIDO */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: { xs: -15, sm: -20 },
                    height: { xs: "220px", sm: "260px" },
                    width: { xs: "180px", sm: "220px" }, // Límite estricto de ancho para encajar perfectamente con la zona de texto
                    zIndex: 2,
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src={ChicaEquipos}
                    alt="Asesora"
                    style={{
                      height: "100%",
                      width: "100%", // Se ajustará al contenedor estricto que definimos arriba
                      objectFit: "contain",
                      objectPosition: "bottom right",
                      display: "block",
                    }}
                  />
                </Box>
              </MotionBox>
            </AnimatePresence>
          </Box>
        ) : (
          /* ================= DESKTOP VIEW (Intacto) ================= */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "320px 1fr" },
              gap: 4,
              alignItems: "start",
            }}
          >
            {/* Desktop Side Tabs */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {modules.map((mod, i) => (
                <MotionBox
                  key={mod.id}
                  onClick={() => setActive(i)}
                  whileHover={{ x: i === active ? 0 : 4 }}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2.5,
                    borderRadius: "16px",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor:
                      i === active
                        ? "rgba(30,64,175,0.25)"
                        : "rgba(44,95,190,0.07)",
                    bgcolor: i === active ? "rgba(30,64,175,0.05)" : "#fff",
                    boxShadow:
                      i === active ? "0 4px 20px rgba(44,95,190,0.1)" : "none",
                    transition: "all 0.25s ease",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: i === active ? 3 : 0,
                      bgcolor: "#1E40AF",
                      borderRadius: "3px 0 0 3px",
                      transition: "width 0.3s ease",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      color: i === active ? "#1E40AF" : "#CBD5E1",
                      letterSpacing: "0.08em",
                      minWidth: 24,
                      transition: "color 0.25s",
                    }}
                  >
                    {mod.number}
                  </Typography>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      bgcolor:
                        i === active ? "#1E40AF" : "rgba(44,95,190,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "background 0.25s",
                    }}
                  >
                    {cloneElement(mod.icon, {
                      style: {
                        filter:
                          i === active ? "none" : "saturate(0) opacity(0.5)",
                        transition: "filter 0.25s",
                      },
                    })}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        letterSpacing: "0.1em",
                        color: i === active ? "#fff" : "#1E40AF",
                        textTransform: "uppercase",
                        bgcolor: i === active ? "#1E40AF" : "transparent",
                        borderRadius: "12px",
                        px: i === active ? 1 : 0,
                        py: i === active ? 0.25 : 0,
                        display: "inline-block",
                        transition: "all 0.25s",
                        mb: i === active ? 0.5 : 0,
                      }}
                    >
                      {mod.tag}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        color: i === active ? "#1A2B4A" : "#64748B",
                        transition: "color 0.25s",
                        lineHeight: 1.3,
                      }}
                    >
                      {mod.title}
                    </Typography>
                  </Box>
                </MotionBox>
              ))}
            </Box>

            {/* Desktop Right Content Panel */}
            <AnimatePresence mode="wait">
              <MotionBox
                key={active}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                sx={{
                  position: "relative",
                  minHeight: 420,
                  display: "flex",
                  alignItems: "stretch",
                }}
              >
                {/* Capa de Fondo Desktop */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "#F8FAFF",
                    borderRadius: "24px",
                    border: "1px solid rgba(44,95,190,0.09)",
                    overflow: "hidden",
                    zIndex: 0,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -30,
                      right: -30,
                      width: 140,
                      height: 140,
                      opacity: 0.06,
                      pointerEvents: "none",
                    }}
                  >
                    <svg viewBox="0 0 140 140" fill="none">
                      <polygon
                        points="70,5 135,37 135,103 70,135 5,103 5,37"
                        fill="#1E40AF"
                      />
                    </svg>
                  </Box>
                </Box>

                {/* Capa de Contenido Desktop */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    p: { lg: 4, xl: 5 },
                    width: { lg: "48%", xl: "55%" },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      fontWeight: 800,
                      fontSize: "2.2rem",
                      color: "#1A2B4A",
                      lineHeight: 1.1,
                      mb: 1.5,
                    }}
                  >
                    {modules[active].title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#1E40AF",
                      fontWeight: 700,
                      fontSize: "1rem",
                      mb: 3,
                      lineHeight: 1.5,
                    }}
                  >
                    {modules[active].summary}
                  </Typography>

                  <Box
                    sx={{
                      width: "100%",
                      height: "1px",
                      bgcolor: "rgba(44,95,190,0.1)",
                      mb: 3,
                    }}
                  />

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {modules[active].features.map((feat, i) => (
                      <MotionBox
                        key={feat}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.07, duration: 0.3 }}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "6px",
                            bgcolor: "#1E40AF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: "1px",
                          }}
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                          >
                            <path
                              d="M2 5l2.5 2.5L8 3"
                              stroke="white"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Box>
                        <Typography
                          sx={{
                            color: "#334155",
                            fontSize: "0.93rem",
                            lineHeight: 1.5,
                            fontWeight: 500,
                          }}
                        >
                          {feat}
                        </Typography>
                      </MotionBox>
                    ))}
                  </Box>
                </Box>

                {/* Capa de la Imagen Desktop */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: { lg: -60, xl: -20 },
                    height: "calc(100% + 40px)",
                    maxHeight: "520px",
                    zIndex: 2,
                    display: { xs: "none", lg: "block" },
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src={ChicaEquipos}
                    alt="Asesora FactuSysDR"
                    style={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                      objectPosition: "bottom right",
                      display: "block",
                    }}
                  />
                </Box>
              </MotionBox>
            </AnimatePresence>
          </Box>
        )}
      </Container>
    </Box>
  );
}
